import { memo } from 'react';
import type { LineItem } from '../../store/useQuoteStore';
import { useQuoteStore } from '../../store/useQuoteStore';
import { calcItemTotal } from '../../utils/pricing';
import { requisitionState, itemMargin } from '../../utils/requisition';
import { NumericInput } from '../ui';
import type { Vendor } from '../../types';

interface AdhocItemRowProps {
  item: LineItem;
  index: number;
  /** Vendor master data for the purchase-requisition picker. */
  vendors: Vendor[];
  vendorsLoading: boolean;
}

const ROOM_OPTIONS = [
  'Living Room',
  'Dining Room',
  'Kitchen',
  'Service Yard',
  'Master Bedroom',
  'Bedroom 1',
  'Bedroom 2',
  'Bedroom 3',
  'Toilet 1',
  'Toilet 2',
  'Other',
];

const UOM_OPTIONS = ['each', 'set', 'panel', 'pair', 'lot', 'hour', 'm', 'sqft'];

/**
 * AdhocItemRow — calculator card for a free-text, non-catalog line item.
 * Staff type a description + unit (sell) price + qty + UOM; there is no
 * category/product/dimension flow. Priced unit price × qty (see calcItemTotal).
 *
 * The card also carries the item's purchase requisition (Phase C): a vendor
 * and the per-unit cost WSS pays them. This is cost-first — staff get the
 * vendor's price before the customer quote goes out — and the margin readout
 * is internal only, never rendered on the customer-facing quote.
 */
function AdhocItemRowImpl({ item, index, vendors, vendorsLoading }: AdhocItemRowProps) {
  const updateItem = useQuoteStore((s) => s.updateItem);
  const removeItem = useQuoteStore((s) => s.removeItem);
  const duplicateItem = useQuoteStore((s) => s.duplicateItem);
  const moveItem = useQuoteStore((s) => s.moveItem);
  const itemCount = useQuoteStore((s) => s.items.length);
  const addAdhocItem = useQuoteStore((s) => s.addAdhocItem);
  const addItem = useQuoteStore((s) => s.addItem);

  const total = calcItemTotal(item);

  const reqState = requisitionState(item);
  const reqBadge =
    reqState === 'QUOTED'
      ? { label: 'Cost confirmed', cls: 'bg-green-100 text-green-700' }
      : { label: 'Cost pending', cls: 'bg-amber-100 text-amber-700' };

  const margin = itemMargin(item);
  const hasCost = margin.cost > 0;
  const marginTone = margin.unitMargin >= 0 ? 'text-green-700' : 'text-red-600';

  const handleVendorChange = (vendorId: string) => {
    const picked = vendors.find((v) => v.id === vendorId);
    updateItem(item.id, 'vendorId', vendorId);
    updateItem(item.id, 'vendorName', picked?.displayName ?? '');
  };

  return (
    <div
      className={`border border-primary/40 rounded-xl p-5 hover:border-primary/60 focus-within:border-primary transition-colors ${
        // Zebra tint by cart position (same rule as LineItemRow), kept in the adhoc orange family.
        index % 2 === 1 ? 'bg-primary/15' : 'bg-primary/5'
      }`}
    >
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <span className="bg-secondary text-white text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded">
            Item {String(index + 1).padStart(2, '0')}
          </span>
          <span className="text-xs font-bold text-primary uppercase tracking-wider flex items-center gap-1">
            <span className="material-symbols-outlined text-[14px]">edit_note</span>
            Adhoc / Custom
          </span>
        </div>
        <span className="font-extrabold text-secondary text-xl">${total.toFixed(2)}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <label className="flex flex-col gap-1.5 md:col-span-12">
          <span className="text-[10px] font-bold text-outline uppercase tracking-wider">
            Description
          </span>
          <input
            type="text"
            placeholder="e.g. Custom door support frame"
            className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white"
            value={item.adhocDescription || ''}
            onChange={(e) => updateItem(item.id, 'adhocDescription', e.target.value)}
          />
        </label>

        <label className="flex flex-col gap-1.5 md:col-span-4">
          <span className="text-[10px] font-bold text-outline uppercase tracking-wider">Room</span>
          <select
            className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white"
            value={item.room || ''}
            onChange={(e) => updateItem(item.id, 'room', e.target.value)}
          >
            <option value="">Select Room...</option>
            {ROOM_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>

        {item.room === 'Other' && (
          <label className="flex flex-col gap-1.5 md:col-span-4">
            <span className="text-[10px] font-bold text-outline uppercase tracking-wider">
              Specify Room
            </span>
            <input
              type="text"
              placeholder="e.g. Balcony"
              className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white"
              value={item.otherRoom || ''}
              onChange={(e) => updateItem(item.id, 'otherRoom', e.target.value)}
            />
          </label>
        )}

        <label className="flex flex-col gap-1.5 md:col-span-2">
          <span className="text-[10px] font-bold text-outline uppercase tracking-wider">Qty</span>
          <NumericInput
            className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white"
            value={String(item.qty)}
            onChange={(v) => updateItem(item.id, 'qty', parseInt(v) || 1)}
          />
        </label>

        <label className="flex flex-col gap-1.5 md:col-span-3">
          <span className="text-[10px] font-bold text-outline uppercase tracking-wider">
            Unit of Measure
          </span>
          <select
            className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white"
            value={item.adhocUom || 'each'}
            onChange={(e) => updateItem(item.id, 'adhocUom', e.target.value)}
          >
            {UOM_OPTIONS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5 md:col-span-3">
          <span className="text-[10px] font-bold text-outline uppercase tracking-wider">
            Unit Price (SGD)
          </span>
          <NumericInput
            decimal
            placeholder="e.g. 250.00"
            className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white"
            value={item.adhocUnitPrice || ''}
            onChange={(v) => updateItem(item.id, 'adhocUnitPrice', v)}
          />
        </label>

        <label className="flex flex-col gap-1.5 md:col-span-12">
          <span className="text-[10px] font-bold text-outline uppercase tracking-wider">
            Remarks{' '}
            <span className="text-outline/60 font-normal normal-case">(shown to customer)</span>
          </span>
          <textarea
            rows={2}
            placeholder="e.g. Colour to match existing frames; deliver after 6pm"
            className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white resize-y"
            value={item.remarks || ''}
            onChange={(e) => updateItem(item.id, 'remarks', e.target.value)}
          />
        </label>
      </div>

      {/* Purchase requisition — internal cost capture, never shown to the customer. */}
      <div className="mt-5 pt-4 border-t border-outline-variant">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <span className="text-xs font-bold text-secondary uppercase tracking-wider flex items-center gap-1">
            <span className="material-symbols-outlined text-[14px]">receipt_long</span>
            Purchase Requisition
          </span>
          <span
            className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded ${reqBadge.cls}`}
          >
            {reqBadge.label}
          </span>
        </div>
        <p className="text-[10px] text-outline mb-3">
          Internal only — record the supplying vendor and the cost they quote you, before this
          quotation goes to the customer. Not shown on the customer&apos;s quote.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <label className="flex flex-col gap-1.5 md:col-span-5">
            <span className="text-[10px] font-bold text-outline uppercase tracking-wider">
              Supplying Vendor
            </span>
            <select
              className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white disabled:opacity-60"
              value={item.vendorId || ''}
              disabled={vendorsLoading}
              onChange={(e) => handleVendorChange(e.target.value)}
            >
              <option value="">In-house / no vendor</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.displayName}
                </option>
              ))}
            </select>
            {!vendorsLoading && vendors.length === 0 && (
              <span className="text-[10px] text-outline">
                No vendors yet — add suppliers in the Vendors section.
              </span>
            )}
          </label>

          <label className="flex flex-col gap-1.5 md:col-span-3">
            <span className="text-[10px] font-bold text-outline uppercase tracking-wider">
              Purchase Cost / unit (SGD)
            </span>
            <NumericInput
              decimal
              placeholder="vendor's price"
              className="border border-outline-variant rounded-lg p-2 text-sm outline-none focus:border-primary bg-white"
              value={item.purchaseCost || ''}
              onChange={(v) => updateItem(item.id, 'purchaseCost', v)}
            />
          </label>

          <div className="md:col-span-4 flex flex-col gap-1.5">
            <span className="text-[10px] font-bold text-outline uppercase tracking-wider">
              Margin
            </span>
            {hasCost ? (
              <div className="rounded-lg border border-outline-variant bg-white px-3 py-2">
                <div className="flex justify-between text-sm">
                  <span className="text-outline">Per unit</span>
                  <span className={`font-bold ${marginTone}`}>
                    ${margin.unitMargin.toFixed(2)} ({(margin.marginPct * 100).toFixed(0)}%)
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-0.5">
                  <span className="text-outline">Total (×{item.qty})</span>
                  <span className={`font-bold ${marginTone}`}>
                    ${margin.totalMargin.toFixed(2)}
                  </span>
                </div>
                {margin.unitMargin < 0 && (
                  <p className="text-[10px] text-red-600 font-bold mt-1">
                    Sell price is below cost.
                  </p>
                )}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-outline-variant px-3 py-2 text-xs text-outline">
                Enter the vendor&apos;s purchase cost to see margin.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-outline-variant flex flex-wrap items-center gap-2">
        <button
          onClick={addAdhocItem}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-container text-primary font-bold text-sm hover:opacity-90 transition-opacity"
        >
          <span className="material-symbols-outlined text-base">add</span>
          Add Adhoc Item
        </button>
        <button
          onClick={addItem}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-primary/40 text-primary font-bold text-sm hover:bg-primary/5 transition-colors"
        >
          <span className="material-symbols-outlined text-base">add</span>
          Add Item
        </button>
        <button
          type="button"
          onClick={() => moveItem(item.id, 'up')}
          disabled={index === 0}
          title="Move up"
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-outline-variant text-secondary font-bold text-sm hover:border-primary hover:text-primary transition-colors disabled:opacity-40 disabled:hover:border-outline-variant disabled:hover:text-secondary"
        >
          <span className="material-symbols-outlined text-base">arrow_upward</span>
          Move up
        </button>
        <button
          type="button"
          onClick={() => moveItem(item.id, 'down')}
          disabled={index === itemCount - 1}
          title="Move down"
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-outline-variant text-secondary font-bold text-sm hover:border-primary hover:text-primary transition-colors disabled:opacity-40 disabled:hover:border-outline-variant disabled:hover:text-secondary"
        >
          <span className="material-symbols-outlined text-base">arrow_downward</span>
          Move down
        </button>
        <button
          onClick={() => duplicateItem(item.id)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-outline-variant text-secondary font-bold text-sm hover:border-primary hover:text-primary transition-colors"
        >
          <span className="material-symbols-outlined text-base">content_copy</span>
          Duplicate
        </button>
        <button
          onClick={() => removeItem(item.id)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-outline-variant text-outline font-bold text-sm hover:border-red-400 hover:text-red-500 transition-colors ml-auto"
        >
          <span className="material-symbols-outlined text-base">delete</span>
          Delete
        </button>
      </div>
    </div>
  );
}

export default memo(AdhocItemRowImpl);
